import React, { useState, useEffect, useRef, useCallback } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import ChatiaDialog, { ChatiaPromptField } from "../ChatiaDialog";
import {
  InputAdornment, IconButton, Typography,
  Chip, Grid, LinearProgress
} from "@material-ui/core";
import {
  FormControl as MuiFormControl,
  InputLabel as MuiInputLabel,
  MenuItem as MuiMenuItem,
  Select as MuiSelect,
  Stack
} from "@mui/material";
import { Bot, Check, Copy, Eye, EyeOff, Plus, Settings2 } from "lucide-react";
import CustomToggleSwitch from "../CustomToggleSwitch";
import api from "../../services/api";

const modelOptions = {
  openai: [
    "gpt-5.5",
    "gpt-4.1",
    "gpt-4.1-mini",
    "gpt-4.1-nano",
    "gpt-4o",
    "gpt-4o-mini",
    "gpt-3.5-turbo-1106",
  ],
  gemini: [
    "gemini-2.5-pro",
    "gemini-2.5-flash",
    "gemini-2.0-flash",
    "gemini-2.0-pro",
  ],
};

const providerOptions = [
  { value: "openai", label: "OpenAI" },
  { value: "gemini", label: "Gemini" },
];

const transferPromptText = `==================================================
REGRA DE TRANSFERÊNCIA PARA HUMANO
==================================================

Se em qualquer momento da conversa o cliente:
- pedir para falar com uma pessoa real
- pedir um atendente humano
- disser que quer falar com alguém
- demonstrar frustração e quiser sair do bot
- disser "humano", "atendente", "pessoa real"

Você DEVE:
1. Responder com empatia e naturalidade
2. Chamar a função transfer_to_human()
3. NÃO tentar convencer o cliente a continuar
4. NÃO fazer mais perguntas
5. NÃO ignorar o pedido

Exemplo de resposta antes de transferir:
"Claro! Vou te conectar com um dos nossos
especialistas agora mesmo."`;

const useStyles = makeStyles((theme) => ({
  root: { display: "flex", flexWrap: "wrap" },
  content: {
    gap: 12,
    display: "flex",
    flexDirection: "column",
  },
  infoCard: {
    background: theme.mode === "dark" ? "#16191f" : "#ffffff",
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    borderRadius: 12,
    padding: 16,
    boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
  },
  cardHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: 0.1,
  },
  cardIcon: {
    width: 32,
    height: 32,
    borderRadius: 10,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
    border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
    color: theme.palette.primary.main,
    "& svg": {
      color: theme.palette.primary.main,
      stroke: theme.palette.primary.main,
    },
  },
  toggleCard: {
    display: "flex",
    flexDirection: "column",
    padding: "10px 16px",
    borderRadius: 10,
    border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
    background: theme.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(15,23,42,0.02)",
    marginBottom: 8,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.05)" : "rgba(15,23,42,0.04)",
      borderColor: theme.mode === "dark" ? "rgba(255,255,255,0.20)" : "rgba(15,23,42,0.20)",
    },
  },
  toggleCardRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    gap: 12,
  },
  toggleLabel: {
    fontSize: "0.9rem",
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    fontWeight: 500,
  },
  toggleDesc: {
    fontSize: "0.75rem",
    color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    marginTop: 2,
  },
  toggleContent: {
    paddingTop: 12,
  },
  checkboxLabel: {
    display: "inline-flex",
    alignItems: "center",
    cursor: "pointer",
    userSelect: "none",
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    fontSize: 14,
  },
  cyberpunkCheckbox: {
    appearance: "none",
    WebkitAppearance: "none",
    MozAppearance: "none",
    width: 16,
    height: 16,
    border: `2px solid ${theme.palette.primary.main}`,
    borderRadius: 5,
    backgroundColor: "transparent",
    display: "inline-block",
    position: "relative",
    margin: 0,
    marginRight: 8,
    padding: 0,
    cursor: "pointer",
    flexShrink: 0,
    "&::before": {
      content: '""',
      backgroundColor: theme.palette.primary.main,
      display: "block",
      position: "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%) scale(0)",
      width: 8,
      height: 8,
      borderRadius: 3,
      transition: "all 0.3s ease-in-out",
    },
    "&:checked::before": {
      transform: "translate(-50%, -50%) scale(1)",
    },
  },
  sectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.mode === "dark" ? "#e9edef" : "#111b21",
  },
  routeChip: {
    margin: 3,
    fontWeight: 600,
  },
  promptHintBox: {
    marginTop: theme.spacing(1.5),
    padding: theme.spacing(1.5),
    backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
    border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(15,23,42,0.10)",
    borderRadius: 8,
    position: "relative",
  },
  promptHintLabel: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(0.5),
  },
  promptHintText: {
    fontSize: 11.5,
    color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    fontFamily: "monospace",
    whiteSpace: "pre-wrap",
    lineHeight: 1.5,
  },
  copyBtn: {
    position: "absolute",
    top: 8,
    right: 8,
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    borderRadius: 8,
    padding: "3px 9px",
    border: theme.mode === "dark" ? "1px solid rgba(255,255,255,0.10)" : "1px solid rgba(15,23,42,0.10)",
    backgroundColor: theme.mode === "dark" ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.04)",
    color: theme.mode === "dark" ? "#d1d5db" : "#4b5563",
    fontSize: 12,
    fontFamily: "monospace",
    cursor: "pointer",
    transition: "all 0.2s",
    "&:hover": {
      color: theme.palette.primary.main,
      borderColor: "rgba(255,255,255,0.18)",
      backgroundColor: "rgba(255,255,255,0.09)",
    },
  },
  copyBtnDone: {
    backgroundColor: "rgba(46, 168, 90, 0.18)",
    borderColor: "rgba(129, 199, 132, 0.35)",
    color: "#81c784",
    "&:hover": {
      color: "#81c784",
      backgroundColor: "rgba(46, 168, 90, 0.22)",
      borderColor: "rgba(129, 199, 132, 0.45)",
    },
  },
}));

const FlowBuilderAIAgentModal = ({ open, onSave, data, onUpdate, close }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMounted = useRef(true);

  const initialState = {
    name: "",
    agentDisplayName: "",
    prompt: "",
    aiProvider: "openai",
    model: "gpt-4o-mini",
    apiKey: "",
    maxTokens: 500,
    temperature: 0.7,
    maxMessages: 20,
    timeoutSeconds: 120,
    enableRouting: false,
    enableFields: false,
    enableTransfer: false,
    routingOptions: [],
    fieldsDatasetId: null,
    fields: [],
    transferMessage: "Vou transferir você para um atendente. Aguarde um momento.",
    enableRAG: false,
    knowledgeBaseIds: [],
    ragTopK: 5,
    ragMinScore: 0.7,
    enableMemory: false,
    memoryExtractFacts: true,
    memoryMaxInteractions: 5,
    enableDatasets: false,
    datasetIds: [],
    datasetTopK: 10,
    enableMedia: false,
    mediaItems: [],
    messageBufferSeconds: 0,
    enableAppointments: false,
  };

  const [config, setConfig] = useState(initialState);
  const [showApiKey, setShowApiKey] = useState(false);
  const [newRouteText, setNewRouteText] = useState("");
  const [transferPromptCopied, setTransferPromptCopied] = useState(false);
  const [labels, setLabels] = useState({ title: "Adicionar Agente IA", btn: "Adicionar" });
  const [qualDatasets, setQualDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);

  const loadDatasets = useCallback(async () => {
    setLoadingDatasets(true);
    try {
      const { data } = await api.get("/datasets", { params: { purpose: "qualification" } });
      if (isMounted.current) {
        setQualDatasets(data.filter(ds => ds.status === "active"));
      }
    } catch (err) {
      if (isMounted.current) {
        setQualDatasets([]);
      }
    }
    if (isMounted.current) {
      setLoadingDatasets(false);
    }
  }, []);

  useEffect(() => {
    if (open === "create" || open === "edit") {
      isMounted.current = true;
      loadDatasets();
    }
    return () => { isMounted.current = false; };
  }, [open, loadDatasets]);

  useEffect(() => {
    if (open === "edit") {
      setLabels({ title: "Editar Agente IA", btn: "Salvar" });
      const existing = data?.data?.typebotIntegration || {};
      const inferredProvider = existing.aiProvider || (/^gemini-/i.test(existing.model || "") ? "gemini" : "openai");
      const availableModels = modelOptions[inferredProvider] || modelOptions.openai;
      setConfig({
        ...initialState,
        ...existing,
        aiProvider: inferredProvider,
        model: availableModels.includes(existing.model) ? existing.model : availableModels[0],
      });
    } else if (open === "create") {
      setLabels({ title: "Adicionar Agente IA", btn: "Adicionar" });
      setConfig(initialState);
    }
  }, [open, data]);

  const handleClose = () => close(null);

  const selectMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        borderRadius: "14px",
        boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
        border: "none",
        backgroundImage: "none",
        overflow: "hidden",
        "& .MuiMenu-list": {
          padding: "6px",
        },
        "& .MuiMenuItem-root": {
          borderRadius: "8px",
          padding: "9px 12px",
          marginBottom: "4px",
          fontSize: 14,
          fontWeight: 500,
          "&.Mui-selected": {
            background: "rgba(6, 207, 156, 0.10)",
          },
          "&.Mui-selected:hover": {
            background: "rgba(6, 207, 156, 0.16)",
          },
        },
      },
    },
  };

  const selectFieldSx = {
    mt: "8px",
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px !important",
      minHeight: 40,
      color: theme.mode === "dark" ? "#e9edef" : "#111b21",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "12px !important",
      borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.18)" : "rgba(15, 23, 42, 0.18)",
    },
    "& .MuiInputBase-root": {
      borderRadius: "12px !important",
    },
    "& .MuiInputLabel-root": {
      backgroundColor: theme.mode === "dark" ? "#16191f" : "#ffffff",
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
      px: "4px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
    "& .MuiSvgIcon-root": {
      color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
    },
  };

  const handleChange = (key, value) => {
    setConfig(prev => ({ ...prev, [key]: value }));
  };

  const handleProviderChange = (provider) => {
    const models = modelOptions[provider] || modelOptions.openai;
    setConfig(prev => ({
      ...prev,
      aiProvider: provider,
      model: models.includes(prev.model) ? prev.model : models[0],
    }));
  };

  const addRoute = () => {
    const text = newRouteText.trim();
    if (!text || config.routingOptions.includes(text)) return;
    setConfig(prev => ({ ...prev, routingOptions: [...prev.routingOptions, text] }));
    setNewRouteText("");
  };

  const removeRoute = (index) => {
    setConfig(prev => ({
      ...prev,
      routingOptions: prev.routingOptions.filter((_, i) => i !== index)
    }));
  };

  const handleFieldsDatasetChange = (dsId) => {
    handleChange("fieldsDatasetId", dsId);
    const ds = qualDatasets.find(d => d.id === dsId);
    if (ds && ds.columns) {
      const fields = ds.columns.map(col => ({
        name: col.id || col.name,
        label: col.label || col.name,
        type: col.type === "select" ? "text" : (col.type || "text"),
        required: false,
        mode: col.mode || "ask",
        description: col.type === "select" && col.options?.length
          ? `Valores: ${col.options.join(", ")}`
          : "",
      }));
      handleChange("fields", fields);
    }
  };

  const handleCopyTransferPrompt = () => {
    try {
      navigator.clipboard.writeText(transferPromptText);
    } catch (err) { }
    setTransferPromptCopied(true);
    setTimeout(() => setTransferPromptCopied(false), 2000);
  };

  const handleSave = () => {
    if (!config.name || !config.prompt || !config.apiKey) return;

    const promptData = { ...config };

    if (open === "edit") {
      onUpdate({
        ...data,
        data: {
          typebotIntegration: promptData,
          timeoutSeconds: config.timeoutSeconds || 0
        },
      });
    } else if (open === "create") {
      onSave({
        typebotIntegration: promptData,
        timeoutSeconds: config.timeoutSeconds || 0
      });
    }
    handleClose();
  };

  const CardTitle = ({ icon: Icon, children }) => (
    <div className={classes.cardHeader}>
      <span className={classes.cardIcon}>
        <Icon size={16} />
      </span>
      <span>{children}</span>
    </div>
  );

  const ToggleSection = ({ label, description, checked, onChange, children }) => (
    <div className={classes.toggleCard}>
      <div className={classes.toggleCardRow}>
        <div>
          <div className={classes.toggleLabel}>{label}</div>
          {description && <div className={classes.toggleDesc}>{description}</div>}
        </div>
        <CustomToggleSwitch
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
      </div>
      {checked && children && <div className={classes.toggleContent}>{children}</div>}
    </div>
  );

  const currentModels = modelOptions[config.aiProvider] || modelOptions.openai;

  return (
    <div className={classes.root}>
      <ChatiaDialog
        open={open === "create" || open === "edit"}
        onClose={handleClose}
        title={labels.title}
        maxWidth="md"
        onSave={handleSave}
        saveText={labels.btn}
        saveDisabled={!config.name || !config.prompt || !config.apiKey}
        contentDividers={false}
      >
        <Stack className={classes.content}>
          <Stack className={classes.infoCard}>
            <CardTitle icon={Bot}>Configuração Base</CardTitle>

            <TextField
              label="Título do Card"
              value={config.name}
              onChange={(e) => handleChange("name", e.target.value)}
              variant="outlined"
              margin="dense"
              fullWidth
              required
              placeholder="Ex: Recepcionista, Triagem, Vendas..."
              helperText="Identifica este agente no fluxo"
            />

            <TextField
              label="Nome do Agente (assinatura)"
              value={config.agentDisplayName || ""}
              onChange={(e) => handleChange("agentDisplayName", e.target.value)}
              variant="outlined"
              margin="dense"
              fullWidth
              placeholder="Ex: Ana, Assistente Virtual, Suporte IA..."
              helperText="Opcional - aparece como assinatura nas mensagens enviadas"
            />

            <Grid container spacing={1}>
              <Grid item xs={12} sm={6}>
                <MuiFormControl variant="outlined" fullWidth margin="dense" sx={selectFieldSx}>
                  <MuiInputLabel>Provedor</MuiInputLabel>
                  <MuiSelect
                    value={config.aiProvider}
                    onChange={(e) => handleProviderChange(e.target.value)}
                    label="Provedor"
                    MenuProps={selectMenuProps}
                  >
                    {providerOptions.map((provider) => (
                      <MuiMenuItem key={provider.value} value={provider.value}>
                        <span style={{ flex: 1 }}>{provider.label}</span>
                      </MuiMenuItem>
                    ))}
                  </MuiSelect>
                </MuiFormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiFormControl variant="outlined" fullWidth margin="dense" sx={selectFieldSx}>
                  <MuiInputLabel>Modelo</MuiInputLabel>
                  <MuiSelect
                    value={config.model}
                    onChange={(e) => handleChange("model", e.target.value)}
                    label="Modelo"
                    MenuProps={selectMenuProps}
                  >
                    {currentModels.map((m) => (
                      <MuiMenuItem key={m} value={m}>
                        <span style={{ flex: 1 }}>{m}</span>
                      </MuiMenuItem>
                    ))}
                  </MuiSelect>
                </MuiFormControl>
              </Grid>
            </Grid>

            <TextField
              label={`API Key (${config.aiProvider === "gemini" ? "Gemini" : "OpenAI"})`}
              value={config.apiKey}
              onChange={(e) => handleChange("apiKey", e.target.value)}
              type={showApiKey ? "text" : "password"}
              variant="outlined"
              margin="dense"
              fullWidth
              required
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowApiKey(!showApiKey)} size="small">
                      {showApiKey ? <EyeOff size={18} /> : <Eye size={18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <ChatiaPromptField
              label="Prompt do Sistema"
              value={config.prompt}
              onChange={(e) => handleChange("prompt", e.target.value)}
              rows={5}
              required
              margin="dense"
              placeholder={"Você é a recepcionista virtual da empresa XYZ.\nSeja cordial e profissional.\nSeu objetivo é identificar o que o cliente precisa e direcionar para o setor correto."}
            />

            <Grid container spacing={1}>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Temperatura"
                  value={config.temperature}
                  onChange={(e) => handleChange("temperature", parseFloat(e.target.value) || 0)}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  type="number"
                  inputProps={{ step: 0.1, min: 0, max: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Max Tokens"
                  value={config.maxTokens}
                  onChange={(e) => handleChange("maxTokens", parseInt(e.target.value, 10) || 100)}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Timeout (s)"
                  value={config.timeoutSeconds}
                  onChange={(e) => handleChange("timeoutSeconds", parseInt(e.target.value, 10) || 0)}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  type="number"
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  label="Aguardar msgs (s)"
                  value={config.messageBufferSeconds || 0}
                  onChange={(e) => {
                    let val = parseInt(e.target.value, 10) || 0;
                    if (val > 0 && val < 5) val = 5;
                    if (val > 20) val = 20;
                    handleChange("messageBufferSeconds", val);
                  }}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 20, step: 1 }}
                  helperText="0 = desativado. Entre 5 e 20s acumula mensagens seguidas."
                />
              </Grid>
            </Grid>
          </Stack>

          <Stack className={classes.infoCard}>
            <CardTitle icon={Settings2}>Funcionalidades</CardTitle>

            <ToggleSection
              label="Roteamento Inteligente"
              description="A IA identifica o que o cliente precisa e direciona para o setor correto."
              checked={config.enableRouting}
              onChange={(checked) => handleChange("enableRouting", checked)}
            >
              <Typography variant="caption" style={{ color: theme.mode === "dark" ? "#b8c1cc" : "#667781" }}>
                Adicione os setores/saídas. Cada setor aparecerá como uma saída no card do nó para você conectar:
              </Typography>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8, marginBottom: 8 }}>
                {config.routingOptions.map((opt, idx) => (
                  <Chip
                    key={idx}
                    label={opt}
                    onDelete={() => removeRoute(idx)}
                    color="primary"
                    variant="outlined"
                    className={classes.routeChip}
                  />
                ))}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <TextField
                  value={newRouteText}
                  onChange={(e) => setNewRouteText(e.target.value)}
                  variant="outlined"
                  size="small"
                  placeholder="Nome do setor (ex: Vendas, Suporte, Financeiro)"
                  onKeyPress={(e) => { if (e.key === "Enter") { e.preventDefault(); addRoute(); } }}
                  style={{ flex: 1 }}
                />
                <Button
                  size="small"
                  variant="contained"
                  color="primary"
                  onClick={addRoute}
                  startIcon={<Plus size={18} />}
                  style={{ borderRadius: 12 }}
                >
                  Adicionar
                </Button>
              </div>
            </ToggleSection>

            <ToggleSection
              label="Qualificação do Lead"
              description="A IA coleta informações do cliente durante a conversa e salva no contato."
              checked={config.enableFields}
              onChange={(checked) => handleChange("enableFields", checked)}
            >
              {loadingDatasets && <LinearProgress style={{ marginBottom: 8 }} />}
              <MuiFormControl fullWidth size="small" variant="outlined" sx={selectFieldSx}>
                <MuiInputLabel>Dataset de Qualificação</MuiInputLabel>
                <MuiSelect
                  value={config.fieldsDatasetId || ""}
                  onChange={(e) => handleFieldsDatasetChange(e.target.value)}
                  label="Dataset de Qualificação"
                  MenuProps={selectMenuProps}
                >
                  <MuiMenuItem value=""><em>Nenhum</em></MuiMenuItem>
                  {qualDatasets.map(ds => (
                    <MuiMenuItem key={ds.id} value={ds.id}>
                      <span style={{ flex: 1 }}>{ds.name} ({(ds.columns || []).length} campos)</span>
                    </MuiMenuItem>
                  ))}
                </MuiSelect>
              </MuiFormControl>
              {config.fieldsDatasetId && config.fields?.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <Typography variant="caption" style={{ color: theme.mode === "dark" ? "#b8c1cc" : "#667781", marginBottom: 4, display: "block" }}>
                    Campos que serão coletados:
                  </Typography>
                  {config.fields.map((field, i) => (
                    <Chip
                      key={i}
                      label={`${field.label || field.name}${field.mode === "internal" ? " (interno)" : ""}`}
                      size="small"
                      variant="outlined"
                      style={{ margin: 2, borderColor: field.mode === "internal" ? "#f59e0b" : undefined, color: field.mode === "internal" ? "#f59e0b" : undefined }}
                    />
                  ))}
                </div>
              )}
              {!config.fieldsDatasetId && (
                <Typography variant="body2" style={{ color: theme.mode === "dark" ? "#b8c1cc" : "#667781", fontStyle: "italic", marginTop: 8 }}>
                  Crie um dataset em "Base de Conhecimento" &gt; aba "Datasets" com os campos que deseja coletar.
                </Typography>
              )}
            </ToggleSection>

            <ToggleSection
              label="Transferência para Humano"
              description="Permite que a IA transfira o atendimento para um humano quando necessário."
              checked={config.enableTransfer}
              onChange={(checked) => handleChange("enableTransfer", checked)}
            >
              <TextField
                label="Mensagem de transferência"
                value={config.transferMessage}
                onChange={(e) => handleChange("transferMessage", e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                placeholder="Vou transferir você para um atendente..."
                style={{ marginBottom: 8 }}
              />
              <div className={classes.promptHintBox}>
                <button
                  type="button"
                  className={`${classes.copyBtn} ${transferPromptCopied ? classes.copyBtnDone : ""}`}
                  onClick={handleCopyTransferPrompt}
                  title="Copiar instrução"
                >
                  <span>{transferPromptCopied ? "Copiado" : "Copiar"}</span>
                  {transferPromptCopied ? (
                    <Check size={13} strokeWidth={2.5} />
                  ) : (
                    <Copy size={13} strokeWidth={1.8} />
                  )}
                </button>
                <Typography className={classes.promptHintLabel}>
                  Adicione ao seu prompt:
                </Typography>
                <Typography className={classes.promptHintText}>
                  {transferPromptText}
                </Typography>
              </div>
            </ToggleSection>

            <ToggleSection
              label="Memória do Contato"
              description="Lembra de conversas anteriores e fatos sobre o contato para personalizar o atendimento."
              checked={config.enableMemory}
              onChange={(checked) => handleChange("enableMemory", checked)}
            >
              <label className={classes.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={config.memoryExtractFacts}
                  onChange={(e) => handleChange("memoryExtractFacts", e.target.checked)}
                  className={classes.cyberpunkCheckbox}
                />
                Extrair fatos automaticamente da conversa
              </label>
              <TextField
                label="Máx. interações para lembrar"
                value={config.memoryMaxInteractions}
                onChange={(e) => handleChange("memoryMaxInteractions", parseInt(e.target.value, 10) || 5)}
                variant="outlined"
                size="small"
                fullWidth
                type="number"
                style={{ marginTop: 8 }}
                helperText="Quantas interações anteriores carregar como contexto"
              />
            </ToggleSection>
          </Stack>
        </Stack>
      </ChatiaDialog>
    </div>
  );
};

export default FlowBuilderAIAgentModal;
